import { Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";

import ConfirmDialog from "components/common/ConfirmDialog";
import { pushNotification } from "features/notificationSlice";
import {
  useDeleteSecureFilesMutation,
  useGetSecureFileAccessUrlMutation,
  useGetSecureFilesAccessUrlsMutation,
} from "features/secureUploads/secureUploads";
import { downloadZipFromPresignedEntries } from "utils/browserZipDownload";
import { formatApiError } from "utils/formatApiError";

import AddFilesToProjectDialog from "./AddFilesToProjectDialog";
import { type SelectedFile, uniqueNames, zipName } from "./selectionModel";

export type BulkAction = "add_to_project" | "download" | "delete";

/** What the selection bar does with the selected files, and its dialogs.
 * `onDone` hears of files that left the list; `onGone` of a selection that no
 * longer matches what is there (someone else deleted or moved a file). */
export function useBulkFileActions({
  clientId,
  clientName,
  onDone,
  onGone,
}: {
  clientId: number;
  clientName: string;
  onDone: (ids: number[]) => void;
  onGone: () => void;
}) {
  const dispatch = useDispatch();
  const [getLinks] = useGetSecureFilesAccessUrlsMutation();
  const [getLink] = useGetSecureFileAccessUrlMutation();
  const [deleteFiles, { isLoading: deleting }] = useDeleteSecureFilesMutation();
  const [toDelete, setToDelete] = useState<SelectedFile[] | null>(null);
  const [toMove, setToMove] = useState<SelectedFile[] | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const toast = (message: string, severity: "success" | "error" | "info") =>
    dispatch(pushNotification({ message, severity }));

  const refused = (error: unknown, fallback: string) => {
    const response = error as { status?: number; data?: unknown };
    if (response?.status === 404) {
      toast(
        "Some of these files are no longer here. Select them again.",
        "error",
      );
      onGone();
      return;
    }
    toast(formatApiError(response?.data, fallback), "error");
  };

  const download = async (files: SelectedFile[]) => {
    if (progress) return;
    try {
      if (files.length === 1) {
        const { url } = await getLink({ id: files[0].id }).unwrap();
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      setProgress("Preparing the download...");
      const links = await getLinks(files.map((file) => file.id)).unwrap();
      const names = uniqueNames(links.map((link) => link.file_name));
      const result = await downloadZipFromPresignedEntries(
        links.map((link, index) => ({
          url: link.url,
          relative_path: names[index],
        })),
        zipName(clientName, new Date()),
        (done, total) => setProgress(`Downloading ${done} of ${total}...`),
      );
      if (!result.ok) toast("The files could not be downloaded.", "error");
    } catch (error) {
      refused(error, "The files could not be downloaded.");
    } finally {
      setProgress(null);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const ids = toDelete.map((file) => file.id);
    try {
      await deleteFiles(ids).unwrap();
      toast(
        ids.length === 1 ? "File deleted." : `${ids.length} files deleted.`,
        "success",
      );
      setToDelete(null);
      onDone(ids);
    } catch (error) {
      setToDelete(null);
      refused(error, "The files could not be deleted.");
    }
  };

  const run = (action: BulkAction, files: SelectedFile[]) => {
    if (action === "download") void download(files);
    else if (action === "delete") setToDelete(files);
    else setToMove(files);
  };

  const dialogs = (
    <>
      <ConfirmDialog
        open={toDelete !== null}
        title={toDelete?.length === 1 ? "Delete File" : "Delete Files"}
        message={
          <Typography variant="body2">
            <b>
              {toDelete?.length === 1
                ? toDelete[0].file_name
                : `${toDelete?.length ?? 0} files`}
            </b>{" "}
            will be removed for everyone who can see{" "}
            {toDelete?.length === 1 ? "it" : "them"}. An Admin can restore{" "}
            {toDelete?.length === 1 ? "it" : "them"} from Deleted Files.
          </Typography>
        }
        confirmLabel={
          toDelete?.length === 1
            ? "Delete File"
            : `Delete ${toDelete?.length} Files`
        }
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
        loading={deleting}
      />
      <AddFilesToProjectDialog
        files={toMove}
        clientId={clientId}
        onClose={() => setToMove(null)}
        onMoved={onDone}
      />
    </>
  );

  return { run, dialogs, progress };
}
